import {
  NOTIFICATION_CENTER_FILTER_DEFAULTS,
  normalizeNotificationCenterFilterState,
  pickNotificationCenterPersistedFilters,
} from "../config/notificationCenterFilterConfig";

export const NOTIFICATION_CENTER_FILTERS_STORAGE_KEY = "notification_center_filters_param";

/**
 * Shared filter preferences for all NC tabs (browser-wide, not per account).
 */
const LEGACY_NOTIFICATION_CENTER_FILTERS_STORAGE_KEYS = [
  "lovat_notification_center_filters_browser",
  "lovat_notification_center_filters",
];

function flattenByTabPayload(byTab) {
  if (!byTab || typeof byTab !== "object") {
    return {};
  }

  const tabEntries = Object.values(byTab).filter(
    (entry) => entry && typeof entry === "object"
  );

  const deadlinesEntry =
    byTab.deadlines ??
    byTab[/** @type {keyof typeof byTab} */ ("deadlines")] ??
    tabEntries.find((entry) => entry.deadlinesScopeFilter !== undefined);

  const visibilityEntry =
    tabEntries.find((entry) => entry.visibilityFilter !== undefined) ??
    deadlinesEntry ??
    tabEntries[0];

  return {
    visibilityFilter: visibilityEntry?.visibilityFilter,
    deadlinesScopeFilter: deadlinesEntry?.deadlinesScopeFilter,
  };
}

function parseStoredFiltersPayload(parsed) {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }

  if (parsed.byTab && typeof parsed.byTab === "object") {
    return flattenByTabPayload(parsed.byTab);
  }

  return parsed;
}

/**
 * @returns {{ visibilityFilter: string, deadlinesScopeFilter: string }}
 */
export function loadNotificationCenterFiltersFromStorage() {
  if (typeof window === "undefined") {
    return normalizeNotificationCenterFilterState();
  }

  try {
    let raw = window.localStorage.getItem(NOTIFICATION_CENTER_FILTERS_STORAGE_KEY);
    if (!raw) {
      for (const legacyKey of LEGACY_NOTIFICATION_CENTER_FILTERS_STORAGE_KEYS) {
        const legacyRaw = window.localStorage.getItem(legacyKey);
        if (legacyRaw) {
          window.localStorage.setItem(NOTIFICATION_CENTER_FILTERS_STORAGE_KEY, legacyRaw);
          window.localStorage.removeItem(legacyKey);
          raw = legacyRaw;
          break;
        }
      }
    }

    if (!raw) {
      return normalizeNotificationCenterFilterState();
    }

    const normalized = normalizeNotificationCenterFilterState(
      parseStoredFiltersPayload(JSON.parse(raw))
    );
    // Deadlines scope is session-only; always restore product default.
    normalized.deadlinesScopeFilter =
      NOTIFICATION_CENTER_FILTER_DEFAULTS.deadlinesScopeFilter;
    return normalized;
  } catch {
    return normalizeNotificationCenterFilterState();
  }
}

/**
 * @param {Record<string, unknown>} state
 */
export function applyStoredNotificationCenterFiltersToState(state) {
  const stored = loadNotificationCenterFiltersFromStorage();
  state.visibilityFilter = stored.visibilityFilter;
  state.deadlinesScopeFilter =
    NOTIFICATION_CENTER_FILTER_DEFAULTS.deadlinesScopeFilter;
}

/**
 * @param {Record<string, unknown>} state
 */
export function saveNotificationCenterFiltersToStorage(state) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      NOTIFICATION_CENTER_FILTERS_STORAGE_KEY,
      JSON.stringify(pickNotificationCenterPersistedFilters(state))
    );
  } catch {
    // ignore quota / private browsing
  }
}
